import {
    AdviserRiskSummary,
    BranchRiskSummary,
    BrokerRiskSummary,
    ClientRiskSummary,
    CurrentRisk,
    RiskLimits,
    RiskSummaryRecord
} from "../RiskSummaryRecord";
import {flatMap, sum} from "../../../../js/util/UtilFunctions";
import {ArrayUtil} from "../../../../js/util/ArrayUtil";
import {Optional} from "../../../../js/util/Optional";
import {MarginLevel} from "../MarginLevel";
import {RiskRowEntityType} from "../RiskRowEntityType";
import {RiskSortOptions} from "../sorting/RiskSortOptions";
import {ModelFilter} from "../filtering/ModelFilter";
import {NameFilter} from "../filtering/NameFilter";
import {Comparator} from "../../../../types/GenericTypes";

export abstract class RiskRow implements RiskLimits, CurrentRisk {

    protected constructor(public readonly id: number,
                          public readonly name: string,
                          public readonly currentStel: number,
                          public readonly stelLimit: number | null,
                          public readonly currentAsxMargin: number,
                          public readonly currentClientMargin: number,
                          public readonly marginLimit: number | null,
                          public readonly currentCbpl: number,
                          public readonly cbplLimit: number | null,
                          public readonly marginLevel: MarginLevel) {
    }

    public abstract filter(modelFilters: ModelFilter, nameFilters: NameFilter): Optional<RiskRow>;

    public abstract sort(sortOptions: RiskSortOptions): RiskRow;
}

export class DashboardSummaryRow extends RiskRow {

    public filter(filter: ModelFilter, nameFilter: NameFilter): Optional<RiskRow> {
        throw Error("unsupported operation");
    }

    public sort(sortOptions: RiskSortOptions): RiskRow {
        throw Error("unsupported operation");
    }

    public static build(rows: Array<RiskRow>): DashboardSummaryRow {
        const currentStel = rows.map((r) => r.currentStel).reduce(sum, 0);

        const currentAsxMargin = rows.map((r) => r.currentAsxMargin).reduce(sum, 0);
        const currentClientMargin = rows.map((r) => r.currentClientMargin).reduce(sum, 0);

        const currentCbpl = rows.map((r) => r.currentCbpl).reduce(sum, 0);

        return new DashboardSummaryRow(1,
                                       "",
                                       currentStel,
                                       null,
                                       currentAsxMargin,
                                       currentClientMargin,
                                       null,
                                       currentCbpl,
                                       null,
                                       MarginLevel.LEVEL_1);
    }
}

export class BrokerRiskRow extends RiskRow {

    public constructor(id: number,
                       name: string,
                       currentStel: number,
                       stelLimit: number | null,
                       asxMargin: number,
                       clientMargin: number,
                       marginLimit: number | null,
                       currentCbpl: number,
                       cbplLimit: number | null,
                       public readonly branches: ReadonlyArray<BranchRiskRow>) {
        super(id,
              name,
              currentStel,
              stelLimit,
              asxMargin,
              clientMargin,
              marginLimit,
              currentCbpl,
              cbplLimit,
              MarginLevel.LEVEL_1);
    }

    public filter(modelFilters: ModelFilter, nameFilters: NameFilter): Optional<BrokerRiskRow> {

        const valueFilter = modelFilters.brokerFilter;
        const groupFilter = nameFilters.groupFilter;

        const noFilters = groupFilter == null && valueFilter == null;

        const groupFilterPassed = Optional.of(groupFilter)
                                          .map((f) => f(this))
                                          .orElse(true);
        const valueFilterPassed = Optional.of(valueFilter)
                                          .map((f) => f(this))
                                          .orElse(true);

        const failsFilterCheck = !groupFilterPassed || !valueFilterPassed;

        if (noFilters || failsFilterCheck) {
            const filteredBranches = this.branches
                                         .map((br) => (br).filter(modelFilters, nameFilters))
                                         .filter((br) => (br).isPresent())
                                         .map((br) => (br).get());
            if (filteredBranches.length === 0) {
                return Optional.empty();
            } else {
                const row = new BrokerRiskRow(this.id,
                                              this.name,
                                              this.currentStel,
                                              this.stelLimit,
                                              this.currentAsxMargin,
                                              this.currentClientMargin,
                                              this.marginLimit,
                                              this.currentCbpl,
                                              this.cbplLimit,
                                              filteredBranches);
                return Optional.of(row);
            }
        } else {
            return Optional.of(this);
        }
    }

    public sort(sortOptions: RiskSortOptions): BrokerRiskRow {
        const rows = this.branches
                         .map((br) => br.sort(sortOptions))
                         .sort(sortOptions.branchRiskComparator);
        return new BrokerRiskRow(this.id,
                                 this.name,
                                 this.currentStel,
                                 this.stelLimit,
                                 this.currentAsxMargin,
                                 this.currentClientMargin,
                                 this.marginLimit,
                                 this.currentCbpl,
                                 this.cbplLimit,
                                 rows);
    }
}

export type BrokerRiskRows = Array<BrokerRiskRow>;

export class BranchRiskRow extends RiskRow {

    public marginLevel = MarginLevel.LEVEL_1;

    public constructor(id: number,
                       name: string,
                       currentStel: number,
                       stelLimit: number | null,
                       asxMargin: number,
                       clientMargin: number,
                       marginLimit: number | null,
                       currentCbpl: number,
                       cbplLimit: number | null,
                       public readonly brokerId: number,
                       public readonly advisers: ReadonlyArray<AdviserRiskRow>) {
        super(id,
              name,
              currentStel,
              stelLimit,
              asxMargin,
              clientMargin,
              marginLimit,
              currentCbpl,
              cbplLimit,
              MarginLevel.LEVEL_1);
    }

    public filter(modelFilters: ModelFilter, nameFilters: NameFilter): Optional<BranchRiskRow> {

        const valueFilter = modelFilters.branchFilter;
        const groupFilter = nameFilters.groupFilter;

        const noFilters = groupFilter == null && valueFilter == null;

        const groupFilterPassed = Optional.of(groupFilter)
                                          .map((f) => f(this))
                                          .orElse(true);
        const valueFilterPassed = Optional.of(valueFilter)
                                          .map((f) => f(this))
                                          .orElse(true);

        const failsFilterCheck = !groupFilterPassed || !valueFilterPassed;

        if (noFilters || failsFilterCheck) {
            const filteredAdvisers = this.advisers
                                         .map((adv) => adv.filter(modelFilters, nameFilters))
                                         .filter((adv) => adv.isPresent())
                                         .map((adv) => adv.get());
            if (filteredAdvisers.length === 0) {
                return Optional.empty();
            } else {
                const row = new BranchRiskRow(this.id,
                                              this.name,
                                              this.currentStel,
                                              this.stelLimit,
                                              this.currentAsxMargin,
                                              this.currentClientMargin,
                                              this.marginLimit,
                                              this.currentCbpl,
                                              this.cbplLimit,
                                              this.brokerId,
                                              filteredAdvisers);
                return Optional.of(row);
            }
        } else {
            return Optional.of(this);
        }
    }

    public sort(sortOptions: RiskSortOptions): BranchRiskRow {
        const rows = this.advisers
                         .map((adv) => adv.sort(sortOptions))
                         .sort(sortOptions.adviserRiskComparator);
        return new BranchRiskRow(this.id,
                                 this.name,
                                 this.currentStel,
                                 this.stelLimit,
                                 this.currentAsxMargin,
                                 this.currentClientMargin,
                                 this.marginLimit,
                                 this.currentCbpl,
                                 this.cbplLimit,
                                 this.brokerId,
                                 rows);
    }
}

export type BranchRiskRows = Array<BranchRiskRow>;

export class AdviserRiskRow extends RiskRow {

    public marginLevel = MarginLevel.LEVEL_1;

    public constructor(id: number,
                       name: string,
                       currentStel: number,
                       stelLimit: number | null,
                       asxMargin: number,
                       clientMargin: number,
                       marginLimit: number | null,
                       currentCbpl: number,
                       cbplLimit: number | null,
                       public readonly branchId: number,
                       public readonly clients: ReadonlyArray<ClientRiskRow>) {
        super(id,
              name,
              currentStel,
              stelLimit,
              asxMargin,
              clientMargin,
              marginLimit,
              currentCbpl,
              cbplLimit,
              MarginLevel.LEVEL_1);
    }

    public filter(modelFilters: ModelFilter, nameFilters: NameFilter): Optional<AdviserRiskRow> {

        const modelFilter = modelFilters.adviserFilter;
        const nameFilter = nameFilters.groupFilter;

        const modelFilterPassed = modelFilter(this);
        const nameFilterPassed = nameFilter(this);

        if (modelFilterPassed && nameFilterPassed) {
            const filteredClients = this.clients
                                        .map((cl) => cl.filter(modelFilters, nameFilters))
                                        .filter((cl) => cl.isPresent())
                                        .map((cl) => cl.get());
        }

        const failsFilterCheck = !nameFilterPassed || !modelFilterPassed;

        if (failsFilterCheck) {
            const filteredClients = this.clients
                                        .map((cl) => cl.filter(modelFilters, nameFilters))
                                        .filter((cl) => cl.isPresent())
                                        .map((cl) => cl.get());
            if (filteredClients.length === 0) {
                return Optional.empty();
            } else {
                const row = this.copy(filteredClients);
                return Optional.of(row);
            }
        } else {
            return Optional.of(this);
        }
    }

    public sort(sortOptions: RiskSortOptions): AdviserRiskRow {
        const rows = [...this.clients].sort(sortOptions.clientRiskComparator);
        return this.copy(rows);
    }

    private copy(rows: Array<ClientRiskRow>): AdviserRiskRow {
        return new AdviserRiskRow(this.id,
                                  this.name,
                                  this.currentStel,
                                  this.stelLimit,
                                  this.currentAsxMargin,
                                  this.currentClientMargin,
                                  this.marginLimit,
                                  this.currentCbpl,
                                  this.cbplLimit,
                                  this.branchId,
                                  rows);
    }
}

export type AdviserRiskRows = Array<AdviserRiskRow>;

export class ClientRiskRow extends RiskRow {

    public constructor(id: number,
                       name: string,
                       currentStel: number,
                       stelLimit: number | null,
                       asxMargin: number,
                       clientMargin: number,
                       marginLimit: number | null,
                       currentCbpl: number,
                       cbplLimit: number | null,
                       public readonly accountNumber: string,
                       public readonly adviserId: number,
                       marginLevel: MarginLevel) {
        super(id,
              name,
              currentStel,
              stelLimit,
              asxMargin,
              clientMargin,
              marginLimit,
              currentCbpl,
              cbplLimit,
              marginLevel);
    }

    public filter(modelFilters: ModelFilter, nameFilters: NameFilter): Optional<ClientRiskRow> {
        const nameFilter = nameFilters.clientFilter;
        const modelFilter = modelFilters.clientFilter;

        const nameFilterPassed = Optional.of(nameFilter)
                                          .map((f) => f(this))
                                          .orElse(true);
        const modelFilterPassed = Optional.of(modelFilter)
                                          .map((f) => f(this))
                                          .orElse(true);

        const failsFilterCheck = !nameFilterPassed || !modelFilterPassed;

        if (failsFilterCheck) {
            return Optional.empty();
        } else {
            return Optional.of(this);
        }
    }

    public sort(sortOptions: RiskSortOptions): ClientRiskRow {
        return this;
    }
}

export type ClientRiskRows = Array<ClientRiskRow>;

export class RiskSummary {

    private constructor(public readonly rootLevel: RiskRowEntityType,
                        public readonly summary: DashboardSummaryRow,
                        public readonly rows: ReadonlyArray<RiskRow>,
                        public readonly stelScenario: string,
                        public readonly asAt: string,
                        public readonly stelCash: number) {
    }

    public filter(modelFilter: ModelFilter, nameFilter: NameFilter): RiskSummary {

        const filteredRows = this.rows
                                 .map((row) => row.filter(modelFilter, nameFilter))
                                 .filter((row) => row.isPresent())
                                 .map((row) => row.get());

        const summary = DashboardSummaryRow.build(filteredRows);
        return new RiskSummary(this.rootLevel, summary, filteredRows, this.stelScenario, this.asAt, this.stelCash);
    }

    public sort(sortOptions: RiskSortOptions): RiskSummary {
        const comparator = this.comparator(sortOptions);
        const sorted = this.rows.map((r) => r.sort(sortOptions)).sort(comparator);

        const summary = DashboardSummaryRow.build(sorted);
        return new RiskSummary(this.rootLevel, summary, sorted, this.stelScenario, this.asAt, this.stelCash);
    }

    private comparator(sortOptions: RiskSortOptions): Comparator<RiskRow> {
        switch (this.rootLevel) {
            case RiskRowEntityType.ORG:
                return sortOptions.brokerRiskComparator;
            case RiskRowEntityType.BROKER:
                return sortOptions.branchRiskComparator;
            case RiskRowEntityType.BRANCH:
                return sortOptions.adviserRiskComparator;
            case RiskRowEntityType.ADVISER:
                return sortOptions.adviserRiskComparator;
            case RiskRowEntityType.CLIENT:
                return (a, b) => a.name.localeCompare(b.name);
        }
    }

    public get brokers(): Array<BrokerRiskRow> {
        if (this.rootLevel === RiskRowEntityType.ORG) {
            return this.rows as Array<BrokerRiskRow>;
        } else {
            return [];
        }
    }

    public get branches(): ReadonlyArray<BranchRiskRow> {
        if (this.rootLevel === RiskRowEntityType.BROKER) {
            return this.rows as ReadonlyArray<BranchRiskRow>;
        } else {
            return this.brokers.map((br) => br.branches).reduce(flatMap, []);
        }
    }

    public get advisers(): ReadonlyArray<AdviserRiskRow> {
        if (this.rootLevel === RiskRowEntityType.BRANCH) {
            return this.rows as ReadonlyArray<AdviserRiskRow>;
        } else {
            return this.branches.map((br) => br.advisers).reduce(flatMap, []);
        }
    }

    public get clients(): ReadonlyArray<ClientRiskRow> {
        if (this.rootLevel === RiskRowEntityType.ADVISER) {
            return this.rows as ReadonlyArray<ClientRiskRow>;
        } else {
            return this.advisers.map((adv) => adv.clients).reduce(flatMap, []);
        }
    }

    public get totalStel(): number {
        const derivativesStel = this.summary.currentStel;
        return this.stelCash + derivativesStel;
    }

    public static build(response: RiskSummaryRecord): RiskSummary {
        const clients = this.buildClients(response.clients);
        const advisers = this.buildAdvisers(response.advisers, clients);
        const branches = this.buildBranches(response.branches, advisers);
        const brokers = this.buildBrokers(response.brokers, branches);

        const stelCash = response.stelCash;

        const rootLevel = RiskSummary.rootLevel(response);
        const rootRows = RiskSummary.rootRows(rootLevel, brokers, branches, advisers, clients);
        const summaryRow = DashboardSummaryRow.build(rootRows);

        return new RiskSummary(rootLevel,
                               summaryRow,
                               rootRows,
                               response.stelScenario,
                               response.asAt,
                               stelCash);
    }

    private static rootLevel(response: RiskSummaryRecord): RiskRowEntityType {
        if (response.brokers.length > 1) {
            return RiskRowEntityType.ORG;
        } else if ((response.brokers.length === 1) || (response.branches.length > 1)) {
            return RiskRowEntityType.BROKER;
        } else if ((response.branches.length === 1) || (response.advisers.length > 1)) {
            return RiskRowEntityType.BRANCH;
        } else {
            return RiskRowEntityType.ADVISER;
        }
    }

    private static rootRows(level: RiskRowEntityType,
                            brokers: Array<BrokerRiskRow>,
                            branches: Array<BranchRiskRow>,
                            advisers: Array<AdviserRiskRow>,
                            clients: Array<ClientRiskRow>): Array<RiskRow> {
        switch (level) {
            case RiskRowEntityType.ORG:
                return brokers;
            case RiskRowEntityType.BROKER:
                return branches;
            case RiskRowEntityType.BRANCH:
                return advisers;
            case RiskRowEntityType.ADVISER:
                return clients;
            case RiskRowEntityType.CLIENT:
                return [];
        }
    }

    private static buildClients(clients: Array<ClientRiskSummary>): ClientRiskRows {
        return clients.map(cl => {
            return new ClientRiskRow(cl.id,
                                     cl.name,
                                     cl.currentStel,
                                     cl.stelLimit,
                                     cl.currentAsxMargin,
                                     cl.currentClientMargin,
                                     cl.marginLimit,
                                     cl.currentCbpl,
                                     cl.cbplLimit,
                                     cl.accountNumber,
                                     cl.adviserId,
                                     cl.marginLevel);
        });
    }

    private static buildAdvisers(advisers: Array<AdviserRiskSummary>, clients: ClientRiskRows): AdviserRiskRows {
        const clientsByAdv = ArrayUtil.groupBy(clients, client => client.adviserId);
        return advisers.map((adv) => {
            const advClients = clientsByAdv[adv.id] || [];
            return new AdviserRiskRow(adv.id,
                                      adv.name,
                                      adv.currentStel,
                                      adv.stelLimit,
                                      adv.currentAsxMargin,
                                      adv.currentClientMargin,
                                      adv.marginLimit,
                                      adv.currentCbpl,
                                      adv.cbplLimit,
                                      adv.branchId,
                                      advClients);
        });
    }

    private static buildBranches(branches: Array<BranchRiskSummary>, advisers: AdviserRiskRows): BranchRiskRows {
        const advisersByBranch = ArrayUtil.groupBy(advisers, adviser => adviser.branchId);
        return branches.map((br) => {
            const brAdvisers = advisersByBranch[br.id] || [];
            return new BranchRiskRow(br.id,
                                     br.name,
                                     br.currentStel,
                                     br.stelLimit,
                                     br.currentAsxMargin,
                                     br.currentClientMargin,
                                     br.marginLimit,
                                     br.currentCbpl,
                                     br.cbplLimit,
                                     br.brokerId,
                                     brAdvisers);
        });
    }

    private static buildBrokers(brokers: Array<BrokerRiskSummary>, branches: BranchRiskRows): BrokerRiskRows {
        const branchByBroker = ArrayUtil.groupBy(branches, branch => branch.brokerId);
        return brokers.map((bro) => {
            const broBranches = branchByBroker[bro.id] || [];
            return new BrokerRiskRow(bro.id,
                                     bro.name,
                                     bro.currentStel,
                                     bro.stelLimit,
                                     bro.currentAsxMargin,
                                     bro.currentClientMargin,
                                     bro.marginLimit,
                                     bro.currentCbpl,
                                     bro.cbplLimit,
                                     broBranches);
        });
    }
}
